/**
 * Subcategory detection multiplexer — runs all 8 category-specific
 * locale-aware detectors against a title and returns their results
 * keyed by category. Callers (web's `ExpenseCategoryService`,
 * mobile's keyword probe loop) pick the winning category from these
 * results.
 *
 * Used to carry a stack of disambiguation helpers (`isObviousFood`,
 * `hasTransportTicketKeywords`, `hasTripContext`, `hasUtilityContext`,
 * `findCategoryOverride`, `findFallbackCategory`, `getSubcategoryInfo`)
 * that patched ML mispredictions for web's old keyword/ML hybrid
 * flow. Stage 11 dropped that flow in favor of keyword + cloud LLM,
 * and those helpers had no remaining callers, so they're gone.
 */
import {
  VALID_ACCOMMODATION_SUBCATEGORIES,
  detectAccommodationSubcategoryWithLocale,
} from './accommodation';
import { SUBCATEGORY_CONFIGS } from './config/subcategoryConfig';
import {
  VALID_DRINKS_SUBCATEGORIES,
  detectDrinksSubcategoryWithLocale,
} from './drinks';
import {
  VALID_ENTERTAINMENT_SUBCATEGORIES,
  detectEntertainmentSubcategoryWithLocale,
} from './entertainment';
import {
  VALID_FOOD_SUBCATEGORIES,
  detectFoodSubcategoryWithLocale,
} from './food';
import {
  VALID_HEALTHCARE_SUBCATEGORIES,
  detectHealthcareSubcategoryWithLocale,
} from './healthcare';
import {
  VALID_SHOPPING_SUBCATEGORIES,
  detectShoppingSubcategoryWithLocale,
} from './shopping';
import {
  VALID_TRANSPORT_SUBCATEGORIES,
  detectTransportSubcategoryWithLocale,
} from './transport';
import {
  VALID_UTILITIES_SUBCATEGORIES,
  detectUtilitiesSubcategoryWithLocale,
} from './utilities';

export interface DetectionResult {
  subcategory: string;
  confidence: number;
  matchedKeywords: string[];
  /** Pre-haircut score; see `SubcategoryResult.matchScore`. */
  matchScore?: number;
}

interface DetectionFunction {
  (title: string, locale: string | undefined | null): DetectionResult;
}

interface SubcategoryModule {
  detect: DetectionFunction;
  valid: string[];
}

class SubcategoryDetectionService {
  private modules: Map<string, SubcategoryModule> = new Map([
    [
      'transport',
      {
        detect: detectTransportSubcategoryWithLocale,
        valid: VALID_TRANSPORT_SUBCATEGORIES,
      },
    ],
    [
      'entertainment',
      {
        detect: detectEntertainmentSubcategoryWithLocale,
        valid: VALID_ENTERTAINMENT_SUBCATEGORIES,
      },
    ],
    [
      'accommodation',
      {
        detect: detectAccommodationSubcategoryWithLocale,
        valid: VALID_ACCOMMODATION_SUBCATEGORIES,
      },
    ],
    [
      'shopping',
      {
        detect: detectShoppingSubcategoryWithLocale,
        valid: VALID_SHOPPING_SUBCATEGORIES,
      },
    ],
    [
      'food',
      {
        detect: detectFoodSubcategoryWithLocale,
        valid: VALID_FOOD_SUBCATEGORIES,
      },
    ],
    [
      'drinks',
      {
        detect: detectDrinksSubcategoryWithLocale,
        valid: VALID_DRINKS_SUBCATEGORIES,
      },
    ],
    [
      'utilities',
      {
        detect: detectUtilitiesSubcategoryWithLocale,
        valid: VALID_UTILITIES_SUBCATEGORIES,
      },
    ],
    [
      'healthcare',
      {
        detect: detectHealthcareSubcategoryWithLocale,
        valid: VALID_HEALTHCARE_SUBCATEGORIES,
      },
    ],
  ]);

  /**
   * Detect subcategories for all configured categories.
   * `locale` is the user's BCP 47 locale tag (e.g. 'en-CA', 'es-CO');
   * each per-category detector resolves it to a primary language and
   * only consults that language's common vocabulary + the global brand
   * registry. When the locale doesn't resolve to a supported language,
   * each detector returns `{ subcategory: 'default', confidence: 0 }`
   * so the cloud LLM tier handles those users.
   */
  detectAllSubcategories(
    title: string,
    locale: string | undefined | null
  ): Map<string, DetectionResult> {
    const detections = new Map<string, DetectionResult>();

    SUBCATEGORY_CONFIGS.forEach(config => {
      const subModule = this.modules.get(config.categoryName);
      if (!subModule) {
        throw new Error(`Unknown subcategory module: ${config.categoryName}`);
      }
      detections.set(config.categoryName, subModule.detect(title, locale));
    });

    return detections;
  }
}

export const subcategoryDetectionService = new SubcategoryDetectionService();
